import { computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useTraits, useAvailability } from '@common-vue/media-card-large';
import { useInitColor } from '@common-vue/media-card-large/src/modules/color';
import { useProductType } from '@composables/useProductType';
import { toTraitId } from '../utils/utils';

/**
 *
 * @param {ParsedColors | null} colorData
 * @returns {Color | null}
 */
function getFirstColor(colorData) {
  return colorData?.colorArray[0] || null;
}

/**
 *
 * @param {Color} color
 * @param {Record<string, boolean>} availability
 * @returns {BaseColor}
 */
function toBaseColor(color, availability) {
  return {
    id: color.id,
    name: color.name,
    spriteOffset: color.swatchImage.swatchSpriteOffset,
    spriteIndex: color.swatchImage.swatchSpriteUrlIndex,
    available: availability[color.id],
  };
}

/**
 * @param {string} id
 * @param {LatchkeyName[]} latchkeyNames
 * @param {Record<string, ParsedColors>} latchkeyColorMap
 */
function useInitEachColor(id, latchkeyNames, latchkeyColorMap) {
  latchkeyNames.forEach((latchkeyName) => {
    const colors = latchkeyColorMap[latchkeyName];
    useInitColor({ id: toTraitId(id, latchkeyName), data: colors });
  });
}

/**
 * @param {string} id
 * @param {import('vue').DeepReadonly<import('vue').Ref<LatchkeyName>>} selectedLatchkey
 * @param {ParsedLatchkeys} parsedLatchkeyData
 * @returns {{
 *  selectedColorId: SelectedColorId,
 *  selectedColor: import('vue').WritableComputedRef<Color | null>
 *  colorProps: import('vue').ComputedRef<ColorProps>
 *  colors: import('vue').ComputedRef<Color[]>
 * }}
 */
export default function useLatchkeyColor(id, selectedLatchkey, { latchkeyNames, latchkeyColorMap }) {
  const store = useStore();
  const { isConsolidatedLatchKey } = useProductType();

  if (isConsolidatedLatchKey) {
    useInitEachColor(id, latchkeyNames, latchkeyColorMap);

    const previousSelectedLatchkey = computed(() => store.state.extraStore.latchkey.previousSelectedLatchkey);

    const traitId = computed(() => toTraitId(id, selectedLatchkey.value));
    const previousTraitId = computed(() => toTraitId(id, previousSelectedLatchkey.value));

    const { selectedColor, selectedColorId } = useTraits(traitId);
    const { selectedColor: previousSelectedColor } = useTraits(previousTraitId);

    watch(selectedLatchkey, (newLatchkey) => {
      const colorData = latchkeyColorMap[newLatchkey];
      const newSelectedColor = colorData?.colorArray.find((color) => color.name === previousSelectedColor.value?.name) || getFirstColor(colorData);
      selectedColorId.value = newSelectedColor?.id || null;
    });

    const availability = computed(() => useAvailability(traitId.value).color.value);

    const colors = computed(() => {
      const colorData = latchkeyColorMap[selectedLatchkey.value || ''] || { colorArray: [] };
      return colorData.colorArray;
    });

    const colorProps = computed(() => {
      const colorData = latchkeyColorMap[selectedLatchkey.value || ''] || {
        colorArray: [],
        spriteUrls: [],
        priceToColors: [],
      };
      return {
        colors: colorData.colorArray.map((color) => toBaseColor(color, availability.value)),
        spriteUrls: colorData.spriteUrls,
        priceToColors: colorData.priceToColors,
      };
    });

    return {
      colors,
      selectedColor,
      selectedColorId,
      colorProps,
    };
  }

  return {
    colors: computed(() => []),
    selectedColor: computed(() => (null)),
    selectedColorId: computed(() => (null)),
    colorProps: computed(() => ({
      colors: [],
      spriteUrls: [],
      priceToColors: [],
    })),
  };
}
